import React from 'react';

/**
 * Lightweight markdown-to-JSX renderer for agent narrative text.
 * Handles the subset the validation agent produces:
 * - ## / ### / #### headings
 * - | markdown tables
 * - **bold**
 * - - / * unordered lists
 * - 1. ordered lists
 * - --- horizontal rules
 * - Emoji prefixes (✅ ❌ ⚠️ 🚨 ℹ️)
 *
 * Does NOT handle images, code blocks, blockquotes, or nested structures.
 */

const renderInlineMarkdown = (text) => {
  if (!text) return text;
  const parts = [];
  let remaining = text;
  let key = 0;

  while (remaining.length > 0) {
    const boldMatch = remaining.match(/\*\*(.+?)\*\*/);
    if (boldMatch) {
      const idx = boldMatch.index;
      if (idx > 0) {
        parts.push(remaining.slice(0, idx));
      }
      parts.push(<strong key={key++}>{boldMatch[1]}</strong>);
      remaining = remaining.slice(idx + boldMatch[0].length);
    } else {
      parts.push(remaining);
      break;
    }
  }
  return parts;
};

const MarkdownRenderer = ({ content }) => {
  if (!content) return null;

  const lines = content.split('\n');
  const elements = [];
  let i = 0;

  while (i < lines.length) {
    const line = lines[i];
    const trimmed = line.trim();

    // Skip empty lines
    if (trimmed === '') {
      i++;
      continue;
    }

    // Horizontal rule
    if (/^-{3,}$/.test(trimmed)) {
      elements.push(<hr key={i} style={{ margin: '12px 0', border: 'none', borderTop: '1px solid #e9ebed' }} />);
      i++;
      continue;
    }

    // Headings
    if (trimmed.startsWith('####')) {
      elements.push(<h4 key={i} style={{ margin: '12px 0 6px', fontSize: '14px' }}>{renderInlineMarkdown(trimmed.slice(4).trim())}</h4>);
      i++;
      continue;
    }
    if (trimmed.startsWith('###')) {
      elements.push(<h3 key={i} style={{ margin: '14px 0 8px', fontSize: '16px' }}>{renderInlineMarkdown(trimmed.slice(3).trim())}</h3>);
      i++;
      continue;
    }
    if (trimmed.startsWith('##')) {
      elements.push(<h2 key={i} style={{ margin: '16px 0 8px', fontSize: '18px' }}>{renderInlineMarkdown(trimmed.slice(2).trim())}</h2>);
      i++;
      continue;
    }

    // Table detection: line starts with |
    if (trimmed.startsWith('|')) {
      const tableLines = [];
      while (i < lines.length && lines[i].trim().startsWith('|')) {
        tableLines.push(lines[i].trim());
        i++;
      }
      // Parse table: first line = header, second = separator (skip), rest = rows
      if (tableLines.length >= 2) {
        const parseRow = (row) =>
          row.split('|').slice(1, -1).map(cell => cell.trim());

        const headers = parseRow(tableLines[0]);
        const isSeparator = (row) => /^\|[\s\-:|]+\|$/.test(row);
        const dataStart = isSeparator(tableLines[1]) ? 2 : 1;
        const rows = tableLines.slice(dataStart).map(parseRow);

        elements.push(
          <div key={`table-${i}`} style={{ overflowX: 'auto', margin: '8px 0' }}>
            <table style={{ borderCollapse: 'collapse', width: '100%', fontSize: '13px' }}>
              <thead>
                <tr>
                  {headers.map((h, hi) => (
                    <th key={hi} style={{ border: '1px solid #d5dbdb', padding: '6px 10px', backgroundColor: '#f2f3f3', textAlign: 'left', fontWeight: 600 }}>
                      {renderInlineMarkdown(h)}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {rows.map((row, ri) => (
                  <tr key={ri}>
                    {row.map((cell, ci) => (
                      <td key={ci} style={{ border: '1px solid #d5dbdb', padding: '6px 10px' }}>
                        {renderInlineMarkdown(cell)}
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        );
      }
      continue;
    }

    // Ordered list: starts with number followed by .
    if (/^\d+\.\s/.test(trimmed)) {
      const listItems = [];
      while (i < lines.length) {
        const l = lines[i];
        const lt = l.trim();
        if (/^\d+\.\s/.test(lt)) {
          listItems.push({ text: lt.replace(/^\d+\.\s/, ''), indent: 0 });
          i++;
        } else if (lt.startsWith('- ') || lt.startsWith('* ')) {
          // Sub-item under numbered list
          listItems.push({ text: lt.slice(2), indent: 1 });
          i++;
        } else if (lt === '' || lt.startsWith('#') || lt.startsWith('|') || /^-{3,}$/.test(lt)) {
          break;
        } else if (l.startsWith('   ') || l.startsWith('\t')) {
          // Continuation / indented content under a list item
          listItems.push({ text: lt, indent: 1 });
          i++;
        } else {
          break;
        }
      }
      elements.push(
        <ol key={`ol-${i}`} style={{ margin: '6px 0', paddingLeft: '24px' }}>
          {listItems.map((item, li) =>
            item.indent === 0 ? (
              <li key={li} style={{ marginBottom: '4px' }}>{renderInlineMarkdown(item.text)}</li>
            ) : (
              <ul key={li} style={{ paddingLeft: '20px', listStyleType: 'disc', margin: '2px 0' }}>
                <li style={{ marginBottom: '2px' }}>{renderInlineMarkdown(item.text)}</li>
              </ul>
            )
          )}
        </ol>
      );
      continue;
    }

    // Unordered list: starts with - or *
    if (trimmed.startsWith('- ') || trimmed.startsWith('* ')) {
      const listItems = [];
      while (i < lines.length) {
        const lt = lines[i].trim();
        if (lt.startsWith('- ') || lt.startsWith('* ')) {
          listItems.push(lt.slice(2));
          i++;
        } else if (lt === '' || lt.startsWith('#') || lt.startsWith('|') || /^-{3,}$/.test(lt) || /^\d+\.\s/.test(lt)) {
          break;
        } else {
          // Continuation line
          listItems.push(lt);
          i++;
        }
      }
      elements.push(
        <ul key={`ul-${i}`} style={{ margin: '6px 0', paddingLeft: '24px' }}>
          {listItems.map((item, li) => (
            <li key={li} style={{ marginBottom: '2px' }}>{renderInlineMarkdown(item)}</li>
          ))}
        </ul>
      );
      continue;
    }

    // Regular paragraph
    elements.push(<p key={i} style={{ margin: '6px 0', lineHeight: '1.5' }}>{renderInlineMarkdown(trimmed)}</p>);
    i++;
  }

  return <div>{elements}</div>;
};

export default MarkdownRenderer;
